"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "What is final expense insurance?",
  answer: "Final expense insurance is a type of whole life policy designed to cover end-of-life costs, such as funeral services, burial or cremation fees, and outstanding medical bills. It provides your family with a tax-free death benefit to ensure they aren't burdened by these expenses during an already difficult time."
}, {
  question: "Do I need a medical exam to qualify?",
  answer: "Most of our plans do not require a medical exam. Approval is typically based on your answers to a few health questions. We work with top-rated carriers to find coverage options that fit your health profile, even if you have pre-existing conditions."
}, {
  question: "How much coverage should I get?",
  answer: "The amount of coverage depends on your personal wishes and the average cost of funerals in your area. Most families choose between $10,000 and $30,000 to ensure all final arrangements are fully covered, leaving nothing for their loved ones to pay out of pocket."
}, {
  question: "Can I change my beneficiary later?",
  answer: "Yes, you maintain full control over your policy. You can update your beneficiary information at any time by contacting your insurance provider. It is important to keep this information current to ensure your wishes are carried out exactly as you intend."
}, {
  question: "How quickly are benefits paid out?",
  answer: "Once a claim is filed and the necessary documentation is received, most insurance companies process payments very quickly—often within a few days. This speed ensures your family has the funds available when they need them most for immediate arrangements."
}, {
  question: "Is my premium guaranteed to stay the same?",
  answer: "Yes, with the whole life policies we offer, your premiums are locked in at the time of purchase. They will never increase due to your age or changes in your health, providing you with long-term financial peace of mind."
}, {
  question: "What happens if I outlive my policy?",
  answer: "Final expense insurance is a permanent policy that stays in effect for your entire life, provided the premiums are paid. It does not expire, ensuring that your family will receive the death benefit whenever the time comes."
}, {
  question: "How do I get started with a quote?",
  answer: "Getting started is simple. You can use our online form to request a personalized quote. Our team will review your information and provide you with the best options available from our network of trusted insurance carriers."
}];
export default function LegalLimitation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="6157" id="legallimitation" className="bg-background py-24 md:py-36 text-foreground">
      <div className="mx-auto max-w-[1320px] px-5 md:px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-12">
          {}
          <div className="lg:col-span-4">
            <div ref={headerRef} className="lg:sticky lg:top-28" style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 700ms cubic-bezier(0.22,1,0.36,1), transform 700ms cubic-bezier(0.22,1,0.36,1)"
          }}>
              <p className="font-default text-xs font-medium uppercase text-muted-foreground" style={{
              letterSpacing: "0.18em"
            }}>
                Frequently Asked Questions
              </p>

              <h2 className="mt-6 font-default text-foreground" style={{
              fontSize: "clamp(2rem, 4vw, 3.5rem)",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.06
            }}>
                Common questions about{" "}
                <em className="font-default italic text-primary" style={{
                fontSize: "clamp(2.25rem, 4.5vw, 4rem)",
                fontWeight: 600
              }}>
                  coverage
                </em>
              </h2>

              <p className="mt-12 font-default text-foreground/80" style={{
              maxWidth: "28ch",
              fontSize: "clamp(1.125rem, 1.5vw, 1.3125rem)",
              fontWeight: 400,
              letterSpacing: "-0.005em",
              lineHeight: 1.55
            }}>
                We provide clear answers to help you make informed decisions about protecting your family's financial future.
              </p>

              <div className="mt-12">
                <Link to="/HomePage" className="group inline-flex items-center font-default text-base font-medium text-primary transition-colors duration-200 hover:text-primary/80">
                  <span className="border-b border-primary pb-1" style={{
                  textUnderlineOffset: "4px"
                }}>
                    Request a consultation
                  </span>
                  <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1.5" aria-hidden="true">
                    →
                  </span>
                </Link>
              </div>
            </div>
          </div>

          {}
          <div ref={listRef} className="lg:col-span-8" style={{
          opacity: listVisible ? 1 : 0,
          transform: listVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 700ms cubic-bezier(0.22,1,0.36,1) 120ms, transform 700ms cubic-bezier(0.22,1,0.36,1) 120ms"
        }}>
            <Accordion type="single" collapsible className="w-full border-t border-border">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-border">
                  <AccordionTrigger className="group flex w-full items-center justify-between gap-6 py-8 text-left font-default text-foreground transition-colors duration-200 hover:text-primary/90 hover:no-underline [&>svg]:hidden" style={{
                fontSize: "clamp(1.0625rem, 1.4vw, 1.25rem)",
                fontWeight: 600,
                lineHeight: 1.3
              }}>
                    <span className="flex-1">{faq.question}</span>
                    <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center text-primary transition-transform duration-200 group-data-[state=open]:rotate-45">
                      <Plus className="size-6" strokeWidth={2} />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pr-12 font-default text-foreground/80" style={{
                maxWidth: "60ch",
                fontSize: "clamp(1.125rem, 1.5vw, 1.3125rem)",
                fontWeight: 400,
                letterSpacing: "-0.005em",
                lineHeight: 1.55,
                paddingTop: "24px"
              }}>
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}